import { ICreateLikeData, IMapyBoxItem, TActivityType, TNoteVersion } from "~/interfaces";
import MyMapsDialog, { IMyMapsDialog } from "./mymaps-dialog";
import { _ } from "~/i18n";
import { createMyMapsReplaceItem } from "../mymaps/replace-item";
import { createMyMapsInput } from "../mymaps/input";
import { createMyMapsNote } from "../mymaps/note";
import { folderCopy, folderCreate, folderUpdate, likeCopy, likeCreate, likeMove, likeUpdate, setFolderData, trackUpdate } from "~/util/providers/mapybox";
import { mapyboxNotification } from "~/util/mapybox";
import { MAX_TITLE_LEN } from "./const";
import { store } from "~/state/store";
import { checkNoteVersion } from "~/util/user-note";
import { createNotePlaceholderPremium } from "../user-note/note-placeholder-premium";
import ActivitySelect from "~/ui/activities/select";
import MyMapsFolderSelectCreate from "../mymaps/folder-select-create";

interface IState {
	usertitle: string;
	note: string;
	trackType: TActivityType;
}

interface IFolderData {
	id: string;
	title: string;
}

interface IMyMapsCreateNote extends Pick<IMyMapsDialog, "onCancel" | "onClose"> {
	mode: "create" | "edit" | "copy" | "replace";
	createData?: ICreateLikeData;
	likeData?: IMapyBoxItem;
	title?: string;
	folder?: {
		id: string;
		title: string;
	};
	enableNote?: boolean;
	isPremiumUser?: boolean;
	noteVersion?: TNoteVersion;
	onCreateFolder?: () => void;
	onSave?: (likeData: Partial<IMapyBoxItem>, folderData: IFolderData) => void;
	onError?: () => void;
}

const DEFAULT_TRACK_TYPE = "pesky";

export default class MyMapsCreateNote {
	_opts: IMyMapsCreateNote;

	_isReplace: boolean;

	_dialog: MyMapsDialog;

	_state: IState;

	_origState: IState;

	_changed: boolean;

	_noteElem: HTMLTextAreaElement;

	_folderSelectCreate: MyMapsFolderSelectCreate;

	_activitySelect: ActivitySelect;

	constructor(data: IMyMapsCreateNote) {
		this._opts = {
			mode: "create",
			title: "",
			onSave: () => {},
			onCancel: () => {},
			onClose: () => {},
			onCreateFolder: () => {},
			onError: () => {},
			enableNote: false,
			isPremiumUser: false,
			noteVersion: "version1",
			...data,
		};
		this._isReplace = this._opts.mode === "replace";
		this._state = {
			usertitle: "",
			note: "",
			trackType: DEFAULT_TRACK_TYPE,
			...this._opts.createData
				? {
					usertitle: this._opts.createData.usertitle || this._opts.createData.title || this._opts.title,
					note: this._opts.createData.description || "",
					...this._opts.createData.type === "path" && this._opts.createData.trackType
						? {
							trackType: this._opts.createData.trackType,
						}
						: {},
				}
				: {},
			...this._opts.likeData
				? {
					usertitle: this._opts.likeData.usertitle || this._opts.likeData.title || this._opts.title,
					note: this._opts.likeData.description || "",
					trackType: (this._opts.likeData.type === "path" || (this._opts.likeData.type === "measure" && this._opts.likeData.data?.readonly) ? this._opts.likeData.data?.info?.trackType : this._opts.likeData.data?.type) || DEFAULT_TRACK_TYPE,
				}
				: {},
		};
		this._origState = {
			...this._state,
		};
		this._changed = false;
		this._noteElem = null;
		this._folderSelectCreate = null;

		const { user } = store.getState().user;

		if (user.state === "green") {
			this._build();
		}
	}

	open() {
		const { user } = store.getState().user;
		const open = () => {
			this._dialog?.open();
			!this._isReplace && this._noteElem?.focus();
		};

		if (user.state === "green") {
			open();
		} else {
			Mapy.getComponent("login").open({
				analyticsData: {
					loginOrigin: "save",
				},
				motivation: "save",
			}).then(() => {
				this._build();
				open();
			}, () => {});
		}
	}

	destroy() {
		this._dialog?.destroy();
		this._activitySelect?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._opts.onClose();
		this._dialog.close(force);
	}

	_build() {
		const dialog = new MyMapsDialog({
			titles: {
				main: this._isReplace ? [_("mymap.saveDetail.updateBtn"), _("mymap.saveDetail.addBtn")] : [_("save")],
				saveBtn: _("save"),
				cancelBtn: _("cancel"),
			},
			onThrowAwayTest: () => this._checkClose(),
			onThrowAwayConfirm: () => {
				this._closeDialog(true);
			},
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._opts.onCancel();
				this._closeDialog(true);
			},
		});
		const contents = dialog.contents;

		let mainContent: HTMLDivElement = null;

		if (this._isReplace) {
			const replaceCont = contents[0];

			replaceCont.classList.add("mymaps__createNoteDialog__replaceContent");
			replaceCont.append(createMyMapsReplaceItem({
				data: this._opts.likeData,
			}));
			mainContent = contents[1];
		} else {
			mainContent = contents[0];
		}

		mainContent.classList.add(this._isReplace ? "mymaps__createNoteDialog__mainContentReplaced" : "mymaps__createNoteDialog__mainContent");

		// titulek
		const titleInput = createMyMapsInput({
			placeholder: _("mymap.addPopup.name"),
			bold: true,
			onChange: usertitle => {
				this._updateState({ usertitle });
			},
			text: this._state.usertitle,
			count: MAX_TITLE_LEN,
		});

		mainContent.append(titleInput.container);

		// vybiratko typu
		if (
			Mapy.CUS.mapybox?.pathTypes
			&& (
				(this._opts.mode === "edit" && (this._opts.likeData?.type === "path" || (this._opts.likeData?.type === "link" && this._opts.likeData?.linktype === "track") || (this._opts.likeData?.type === "measure" && this._opts.likeData?.data?.readonly)))
				|| (this._opts.mode === "create" && this._opts.createData?.type === "path")
			)
		) {
			this._activitySelect = new ActivitySelect({
				selected: this._state.trackType,
				onChange: trackType => {
					// pri editaci by nemelo byt mozne zadat all, ale kdyby se to nahodou stalo, tak to nezaneseme do dat a na server
					if (trackType === "all") {
						return;
					}

					this._updateState({ trackType });
				},
			});
			mainContent.append(this._activitySelect.container);
		}

		this._folderSelectCreate = new MyMapsFolderSelectCreate({
			folder: this._opts.folder,
			disabled: this._opts.mode === "copy" && this._opts.createData?.type === "folder",
			onChange: () => {
				this._updateState({});
			},
		});

		if (!(this._opts.mode === "copy" && this._opts.createData?.type === "folder")) {
			mainContent.append(this._folderSelectCreate.container);
		}

		// poznamka
		if (this._opts.enableNote) {
			if (this._opts.isPremiumUser) {
				const noteData = createMyMapsNote({
					text: this._state.note,
					onChange: note => {
						this._updateState({ note });
					},
				});

				mainContent.append(noteData.container);

				this._noteElem = noteData.textarea;
			} else if (checkNoteVersion(this._opts.noteVersion, "version2")) {
				mainContent.append(createNotePlaceholderPremium({}));
			}
		}

		this._dialog = dialog;
	}

	_updateState(newData: Partial<IState>) {
		Object.assign(this._state, newData);

		this._dialog.setSaveBtnTitle = this._folderSelectCreate.state.createNewFolder ? _("mymap.folders.createAndSave") : _("save");

		this._changed = this._folderSelectCreate.changed
			|| this._state.note !== this._origState.note
			|| this._state.usertitle !== this._origState.usertitle
			|| this._state.trackType !== this._origState.trackType;
	}

	_checkClose() {
		return !this._changed;
	}

	_sendError() {
		this._opts.onError();
		this._closeDialog(true);
	}

	async _onSave() {
		// neni zmena, zrusime
		if (!this._changed && this._opts.likeData && !["replace", "copy", "edit"].includes(this._opts.mode)) {
			this._closeDialog(true);

			return;
		}

		this._dialog.error = "";
		this._dialog.loading = true;

		const folderState = this._folderSelectCreate.state;
		let folderId = folderState.folderId;
		let newLikeData: Partial<IMapyBoxItem> = null;

		// nova slozka
		if (folderState.createNewFolder) {
			if (folderState.newFolderTitle === "") {
				this._dialog.loading = false;
				this._dialog.error = _("mymap.folders.emptyFolder");

				return;
			}

			try {
				const createData = await folderCreate(folderState.newFolderTitle);

				folderId = createData.id;
				mapyboxNotification({
					msg: _("mymap.folders.createOk", { folder: folderState.newFolderTitle }),
				});
			} catch (exc) {
				mapyboxNotification({
					msg: _("mymap.folders.createError"),
					type: "error",
				});

				this._sendError();

				return;
			}
		}

		// presun - mame like data, ale nechceme vytvaret
		if (this._opts.likeData && !this._opts.createData && this._folderSelectCreate.changed) {
			try {
				newLikeData = await likeMove(this._opts.likeData.id, folderId);
				/*mapyboxNotification({
					msg: _("mymap.addPopup.moveOk", { count: 1 }),
					folderTitle: this._state.folderTitle,
					folderId,
				});*/
			} catch (exc) {
				mapyboxNotification({
					msg: _("mymap.addPopup.moveError"),
					type: "error",
				});

				this._sendError();

				return;
			}
		}

		// kopie like/slozka
		if (this._opts.mode === "copy") {
			try {
				if (this._opts.createData.type === "folder") {
					newLikeData = await folderCopy(this._opts.createData.id, "", {
						title: this._state.usertitle,
						description: this._state.note,
					});
					mapyboxNotification({
						msg: _("mymap.addPopup.moveOk"),
						folderTitle: folderState.folderTitle,
						folderId,
					});
				} else {
					// vytvorime
					const likeCopyData = await likeCopy([this._opts.createData.id], this._opts.createData.parent_id);
					const newLikeId = likeCopyData.copied.filter(item => "new_id" in item)[0].new_id;

					// aktualizujeme
					await likeUpdate(newLikeId, {
						...this._opts.createData.title === this._state.usertitle
							? {}
							: {
								usertitle: this._state.usertitle,
							},
						description: this._state.note,
					});

					newLikeData = {
						id: newLikeId,
						usertitle: this._state.usertitle,
						description: this._state.note,
					};
					mapyboxNotification({
						msg: _("mymap.addPopup.moveOk"),
						folderTitle: folderState.folderTitle,
						folderId,
					});
				}
			} catch (exc) {
				mapyboxNotification({
					msg: _("mymap.addPopup.moveError"),
					type: "error",
				});

				this._sendError();

				return;
			}
		// mame data - nahrazeni
		} else if (this._opts.likeData && this._isReplace && this._dialog.activeContent === 0) {
			try {
				const hints = {};
				const copiedData = {
					...this._opts.createData,
				};

				if (copiedData.title === copiedData.usertitle) {
					delete copiedData.usertitle;
				}

				if ("mark" in copiedData) {
					hints["1.mark.lon"] = "float";
					hints["1.mark.lat"] = "float";
				}

				await likeUpdate(this._opts.likeData.id, copiedData, hints);

				newLikeData = {
					id: this._opts.likeData.id,
					usertitle: this._state.usertitle,
					description: this._state.note,
					...this._opts.likeData.type === "path" || (this._opts.likeData.type === "link" && this._opts.likeData.linktype === "track") || (this._opts.likeData.type === "measure" && this._opts.likeData.data?.readonly)
						? {
							trackType: this._state.trackType,
						}
						: {},
				};

				if (this._opts.likeData.type === "path") {
					mapyboxNotification({
						msg: _("path.edit"),
					});
				} else {
					mapyboxNotification({
						msg: _("mymap.saveDetail.updateOk"),
						folderTitle: folderState.folderTitle,
						folderId,
					});
				}
			} catch (exc) {
				mapyboxNotification({
					msg: _("mymap.saveDetail.updateError"),
					type: "error",
				});

				this._sendError();

				return;
			}
		// mame data - update dat
		} else if (this._opts.likeData && !this._isReplace) {
			try {
				if (this._opts.likeData.type === "folder") {
					await folderUpdate(this._opts.likeData.id, {
						usertitle: this._state.usertitle,
						description: this._state.note,
					});
				} else {
					if (Mapy.CUS.mapybox?.pathTypes && this._state.trackType !== this._origState.trackType && this._opts.likeData.type === "link" && this._opts.likeData.linktype === "track") {
						await trackUpdate(this._opts.likeData.linkid, {
							// @ts-ignore
							"data.info.trackType": this._state.trackType,
						});
					}

					await likeUpdate(this._opts.likeData.id, {
						...this._opts.likeData.title === this._state.usertitle
							? {}
							: {
								usertitle: this._state.usertitle,
							},
						description: this._state.note,
						...Mapy.CUS.mapybox?.pathTypes && (this._opts.likeData.type === "path" || (this._opts.likeData.type === "measure" && this._opts.likeData.data?.readonly))
							? {
								// @ts-ignore
								"data.info.trackType": this._state.trackType,
							}
							: {},
					});
				}

				newLikeData = {
					id: this._opts.likeData.id,
					usertitle: this._state.usertitle,
					description: this._state.note,
					...this._opts.likeData.type === "path" || (this._opts.likeData.type === "link" && this._opts.likeData.linktype === "track") || (this._opts.likeData.type === "measure" && this._opts.likeData.data?.readonly)
						? {
							trackType: this._state.trackType,
						}
						: {},
				};

				if (this._opts.likeData.type === "path") {
					mapyboxNotification({
						msg: _("path.edit"),
					});
				} else {
					mapyboxNotification({
						msg: _("mymap.saveDetail.updateOk"),
						folderTitle: folderState.folderTitle,
						folderId,
					});
				}
			} catch (exc) {
				mapyboxNotification({
					msg: _("mymap.saveDetail.updateError"),
					type: "error",
				});

				this._sendError();

				return;
			}
		// vytvoreni
		} else {
			try {
				const createData = {
					...this._opts.createData,
					usertitle: this._state.usertitle,
					description: this._state.note,
					...folderId
						? {
							// eslint-disable-next-line camelcase
							parent_id: folderId,
						}
						: {},
				};
				const hints = {};

				// smazeme usertitle
				if (createData.title === createData.usertitle) {
					delete createData.usertitle;
				}

				if ("mark" in createData) {
					hints["0.mark.lon"] = "float";
					hints["0.mark.lat"] = "float";
				}

				newLikeData = await likeCreate(createData, hints);
				if (Mapy.CUS.mapybox?.pathTypes && this._opts.mode === "create" && this._opts.createData.type === "path") {
					await likeUpdate(newLikeData.id, {
						// @ts-ignore
						"data.info.trackType": this._state.trackType,
					});
				}
				Mapy.getComponent("places").notifyLikeCreate(folderId);
				// eslint-disable-next-line camelcase
				Mapy.getComponent("places").addToLikeList(newLikeData.id, { parent_name: folderState.folderTitle, parent_id: folderId, source: newLikeData.source, id: newLikeData.source_id });
				mapyboxNotification({
					msg: _("mymap.addPopup.createOk"),
					folderTitle: folderState.folderTitle,
					folderId,
				});
			} catch (exc) {
				mapyboxNotification({
					msg: _("mymap.addPopup.createError"),
					type: "error",
				});

				this._sendError();

				return;
			}
		}

		setFolderData({
			id: folderId,
			title: folderState.folderTitle,
		});

		this._opts.onSave(newLikeData, {
			id: folderId,
			title: folderState.folderTitle,
		});
		this._closeDialog(true);
	}
}
